import type { UseMutationOptions } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { KEYS, repository } from '@repositories/Repository';
import type IMfaRepository from '@repositories/mfaRepository/IMfaRepository';
import type { MfaVerifyResponse } from '@repositories/mfaRepository/mfa.types';
import { profileQueryKey, userQueryKey } from './useUser';

const mfaRepo = repository.get<IMfaRepository>(KEYS.MFA_REPOSITORY);

export const mfaQueryKey = {
  all: ['mfa'] as const,
  verify: () => [...mfaQueryKey.all, 'verify'] as const,
  userMfa: (userId: string) => [...mfaQueryKey.all, 'user', userId] as const,
  myMfa: () => [...mfaQueryKey.all, 'my'] as const,
};

/**
 * MFA 인증/설정 검증
 * POST /api/v1/actions/verify-mfa
 *
 * @description
 * - MFA 설정 필요 시 (mfaSetupRequired = true): 최초 설정 검증
 * - MFA 인증 필요 시 (mfaRequired = true): 로그인 시 인증 검증
 * - Response Header에 정상 Access Token이 포함됨
 * - 응답: { verified: boolean, remainingAttempts: number, user?: User }
 * - verified=true: 인증 성공, user 정보 포함
 * - verified=false: 인증 실패, remainingAttempts 확인
 * - 3번 실패 시 401 에러 발생
 */
export const useVerifyMfa = (options?: UseMutationOptions<MfaVerifyResponse, AxiosError, string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (otpCode: string) => {
      const result = await mfaRepo.verifyMfa(otpCode);
      // API.post()에서 이미 동기적으로 토큰을 업데이트함
      return result;
    },
    onSuccess: (data, variables, context) => {
      // 인증 성공 시에만 사용자 관련 쿼리 무효화
      if (data.verified) {
        queryClient.invalidateQueries({ queryKey: userQueryKey.all });
      }
      options?.onSuccess?.(data, variables, context);
    },
    onError: (error, variables, context) => {
      options?.onError?.(error, variables, context);
    },
    ...options,
  });
};

/**
 * 사용자의 MFA 설정 초기화 (관리자용)
 * POST /api/v1/actions/reset-mfa
 *
 * @description
 * 관리자가 특정 사용자의 MFA 설정을 초기화합니다.
 * 해당 사용자는 다음 로그인 시 MFA를 다시 설정해야 합니다.
 */
export const useResetUserMfa = (options?: UseMutationOptions<void, AxiosError, string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      await mfaRepo.updateUserMfa(userId);
    },
    onSuccess: (data, userId, context) => {
      // 해당 사용자의 MFA 설정 무효화
      queryClient.invalidateQueries({ queryKey: mfaQueryKey.userMfa(userId) });
      queryClient.invalidateQueries({ queryKey: profileQueryKey.detail() });
      options?.onSuccess?.(data, userId, context);
    },
    onError: (error, userId, context) => {
      options?.onError?.(error, userId, context);
    },
    ...options,
  });
};

/**
 * 내 MFA 설정 초기화 (본인용)
 * POST /api/v1/actions/reset-my-mfa
 *
 * @description
 * 본인의 MFA 설정을 초기화합니다.
 * 비밀번호 검증 후 MFA를 해제하고, 다음 로그인 시 다시 설정할 수 있습니다.
 */
export const useResetMyMfa = (options?: UseMutationOptions<void, AxiosError, string>) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (password: string) => {
      await mfaRepo.updateMyMfa(password);
    },
    onSuccess: (data, password, context) => {
      // 내 MFA 설정 무효화
      queryClient.invalidateQueries({ queryKey: mfaQueryKey.myMfa() });
      queryClient.invalidateQueries({ queryKey: userQueryKey.all });
      queryClient.invalidateQueries({ queryKey: profileQueryKey.detail() });
      options?.onSuccess?.(data, password, context);
    },
    onError: (error, password, context) => {
      options?.onError?.(error, password, context);
    },
    ...options,
  });
};
